import { PERSONAS_SEED } from '@/seed/personas';
import { MILESTONES } from '@/seed/milestones';
import type { ScenarioConfig } from '@/types/scenario';
import type { SessionRecord } from '@/types/session';

/**
 * Compose the coach's ~2-minute "Play full debrief" narrative — connects the
 * session's wins, drifts, and a specific next step into one spoken thread.
 * Pure function. Called from DebriefScreen and passed to `playResponse()`
 * so the coach speaks it; the dialog flow on the CodeBaby side must NOT also
 * fire a "debrief summary" on mount (single-source rule).
 *
 * Branches the framing on the persona's starting posture so a close with
 * Kayla (warm/eager) doesn't get the same speech as a close with Frank
 * (defensive/hostile). The coach acknowledges what the customer was actually
 * like and reframes missed milestones as practice for the next, harder
 * room — instead of treating every miss as a failure.
 *
 * Target length ≈ 280–340 words (~2 minutes at ~150 wpm).
 */

export function composeDebriefNarrative(session: SessionRecord, scenario: ScenarioConfig): string {
  const persona = PERSONAS_SEED[session.persona_id];
  const first = persona.display_name.split(' ')[0];
  const { object, possessive, subject } = persona.pronouns;
  const wins = session.moments.filter((m) => m.type === 'win');
  const growths = session.moments.filter((m) => m.type === 'growth');
  const closed = session.outcome === 'closed';
  const missed = scenario.milestones.filter((m) => !session.milestones_hit.includes(m));

  // Persona posture from start_temperature. The bands roughly correspond
  // to the three seeded personas — Kayla ~2.5 (warm), Linda ~1.0
  // (skeptical), Frank ~0.3 (defensive) — but it generalises to any
  // future persona with a configured start_temperature.
  const posture: 'warm' | 'skeptical' | 'defensive' =
    scenario.start_temperature >= 2.0
      ? 'warm'
      : scenario.start_temperature >= 1.0
        ? 'skeptical'
        : 'defensive';

  const parts: string[] = [];

  // ----- Opening: name what the customer was actually like ---------------

  if (closed && posture === 'warm') {
    parts.push(
      `Alright — you closed ${first}, and credit where it's due. But ${subject} was leaning in from the first turn. The real question on a session like this is what you'd do with a harder customer, and that's where I want to take this.`,
    );
  } else if (closed && posture === 'skeptical') {
    parts.push(
      `You closed ${first}. ${subject.charAt(0).toUpperCase()}${subject.slice(1)} doesn't open up easy, so this wasn't luck. Let me walk you through what actually moved ${object}.`,
    );
  } else if (closed && posture === 'defensive') {
    parts.push(
      `You closed ${first} — and ${subject} came in cold and skeptical. That's the rep we're trying to build. Let me walk you through what made the difference.`,
    );
  } else if (posture === 'warm') {
    parts.push(
      `You didn't close ${first} this time, even though ${subject} was warm. That tells me the muscle memory isn't there yet — when the customer hands you the room, you have to be ready to take it.`,
    );
  } else if (posture === 'defensive') {
    parts.push(
      `You didn't close ${first}, and ${subject} is the hardest of the three. First attempts here almost never close. What I care about is what you did right and what we're fixing before the next pass.`,
    );
  } else {
    parts.push(
      `You didn't close ${first} this time. That's fine. First attempts rarely close. What I care about is what you did right, and what we're fixing before the next one.`,
    );
  }

  // ----- Wins thread -----------------------------------------------------

  if (wins.length === 0) {
    parts.push(
      posture === 'warm'
        ? `There wasn't a clear win in this session — and ${first} gave you the runway. That's the lesson: a warm customer doesn't run the discovery for you. Every question still has to do work.`
        : `There wasn't a clear win in this session — you played it safe. Safe doesn't move ${posture === 'defensive' ? 'a wall' : 'a skeptic'}. Every question you ask ${object} has to cost ${object} something to NOT answer.`,
    );
  } else {
    const w = wins[0]!;
    parts.push(
      `Your strongest moment came around the ${formatTime(w.at_s)} mark, when you said: "${w.quote}" That's the kind of question that pulls a specific pain out of a vague objection. ${w.coach_note}`,
    );
    if (wins.length > 1) {
      const w2 = wins[1]!;
      parts.push(
        `You did it again at ${formatTime(w2.at_s)} — "${w2.quote}" Two strong moments in one session is a pattern, not an accident. Keep doing that.`,
      );
    }
  }

  // ----- Missed milestones — framed by posture, not as failure -----------

  if (missed.length > 0) {
    const missedLabels = missed.slice(0, 2).map((m) => MILESTONES[m].label);
    const missedSentence =
      missedLabels.length === 1
        ? missedLabels[0]!.toLowerCase()
        : `${missedLabels.slice(0, -1).join(', ').toLowerCase()} and ${missedLabels[missedLabels.length - 1]!.toLowerCase()}`;

    if (closed && posture === 'warm') {
      parts.push(
        `Two moves you skipped: ${missedSentence}. ${first} didn't push you on those — ${subject} didn't have to. Run those drills before you face a tougher room — there, skipping them is the difference between closing and not.`,
      );
    } else if (closed) {
      parts.push(
        `You skipped ${missedSentence}. With ${first} that didn't cost you the deal, but make those reflexes anyway — they're free points on harder rooms.`,
      );
    } else if (posture === 'warm') {
      parts.push(
        `Where it leaked: ${missedSentence}. ${first} was ready to give you those — you just didn't ask. Practice the ask, not the reaction.`,
      );
    } else {
      parts.push(
        `The two moves to rehearse before your next pass: ${missedSentence}. Those are what would have flipped the room.`,
      );
    }
  }

  // ----- Growth moment (specific drift) ----------------------------------

  if (growths.length > 0) {
    const g = growths[0]!;
    parts.push(
      `One specific moment to rehearse: ${g.coach_note} The shape of the fix is in those words.`,
    );
  }

  // ----- Recommendation --------------------------------------------------

  if (closed && posture === 'warm') {
    parts.push(
      `Don't celebrate too long. The next persona won't be the warm-up ${first} was — they'll punish the things you skipped today.`,
    );
  } else if (closed) {
    parts.push(
      `Take a beat. The next persona will test things you didn't need with ${first}. Then jump back in.`,
    );
  } else {
    parts.push(
      `Take another pass at ${first}. Anchor your opening to the number ${subject} gave you this session — use ${possessive} exact language. You already have the raw material. You just need to stay on it longer than last time.`,
    );
  }

  return parts.join(' ');
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
